import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Metrics } from '../metrics/Metrics';
import useComponentMountMetric from '../metrics/useComponentMountMetric';
import { useStableRef } from '../utils/hooks/useStableRef';
import PropertyInputV2 from '../v2/components/PropertyInputV2';
import { EDIT_INPUT_MODE } from '../v2/types/PropertyInputV2Component';
import useConditionalPropertyDependencies from './useConditionalPropertyDependencies';
import { useConditionalPropertyOptionsRules } from './useConditionalPropertyOptionsRules';
import useConditionalPropertyRules from './useConditionalPropertyRules';
import { useGetFormValue } from './useGetFormValue';
import usePropertiesFormApi from './usePropertiesFormApi';
import { getAllPropertyRoots, getDependentFormProperties, getFormPropertiesDifference, getFormPropertyNames, toFormNode } from './utils';
import { useFormValidation } from './validation/hooks/useFormValidation';
import uniqueId from 'transmute/uniqueId';
import { AutosaveContext } from '../autosave/context/AutosaveContext';
import { createElement as _createElement } from "react";
const emptyInputs = [];
const makeNewFormInput = ({
  properties,
  node,
  enableAutosave,
  formId,
  getFormValue,
  getNodeValidation,
  handleChange,
  handleSave,
  handleValidationChange,
  objectId,
  objectTypeId,
  inputMode,
  showPropertyDescription
}) => {
  const {
    propertyName,
    isRequired
  } = node;
  const property = properties[propertyName];

  // Protects the form in case of misspelled or unavailable properties.
  // Some might have been introduced when adding conditional property fields.
  if (!property) {
    return undefined;
  }
  const dependents = [];
  for (const dependentFormProperty of node.dependents) {
    const dependentInput = makeNewFormInput({
      properties,
      node: dependentFormProperty,
      enableAutosave,
      formId,
      getFormValue,
      getNodeValidation,
      handleChange,
      handleSave,
      handleValidationChange,
      objectId,
      objectTypeId,
      inputMode,
      showPropertyDescription
    });
    if (dependentInput !== undefined) {
      dependents.push(dependentInput);
    }
  }

  // Setting value as undefined in an input will convert the input to uncontrolled state.
  // So, falling back to null.
  const value = getFormValue(propertyName) || null;
  return {
    property,
    isRequired,
    dependents,
    validation: getNodeValidation(node.nodeId),
    render: additionalPropertyInputProps => {
      return /*#__PURE__*/_createElement(PropertyInputV2, Object.assign({}, additionalPropertyInputProps, {
        enableAutosave: enableAutosave,
        formMetadata: {
          formId,
          formNodeId: node.nodeId
        },
        onSave: handleSave,
        onChange: handleChange,
        onValidationChange: handleValidationChange,
        getAdditionalPropertyValue: getFormValue,
        enableConditionalPropertiesPanel: false,
        enableConditionalPropertyOptions: true,
        inputMode: inputMode,
        key: property.name,
        objectId: objectId,
        objectTypeId: objectTypeId,
        formControlProps: Object.assign({
          label: property.label
        }, additionalPropertyInputProps ? additionalPropertyInputProps.formControlProps : undefined, {
          required: isRequired,
          help: showPropertyDescription ? property.description : undefined
        }),
        property: property,
        value: value
      }));
    }
  };
};
const propertiesEqualityKey = properties => properties.map(({
  propertyName,
  isRequired
}) => `${propertyName}/${isRequired}`).join(':');
const useNewPropertiesForm = ({
  fetchConditionalPropertyRules,
  objectId,
  objectTypeId,
  onChange,
  properties: propertiesUnstable,
  values,
  alwaysShowConditionalProperties = false,
  alwaysShowConditionalPropertyOptionsWithErrors = false,
  enableAutosave = false,
  onSave,
  inputMode = EDIT_INPUT_MODE,
  showPropertyDescription = false,
  getSavedValue
}) => {
  useComponentMountMetric('useNewPropertiesForm');
  const formId = useMemo(() => uniqueId('form-'), []);
  const [propertyValidations, setPropertyValidations] = useState(new Map());
  const [changedProperties, setChangedProperties] = useState(new Set());
  const [initialValuesCache, setInitialValuesCache] = useState({});
  const onChangeRef = useRef(() => {});
  if (typeof onChange === 'function') {
    onChangeRef.current = onChange;
  }
  const propertiesRef = useStableRef(propertiesUnstable, (prev, next) => propertiesEqualityKey(prev) === propertiesEqualityKey(next));
  const valuesRef = useRef(undefined);
  valuesRef.current = values;
  const formNodesRef = useRef([]);

  // fetches all data and dispatches action with updated data
  const {
    loading,
    error,
    properties,
    conditionalPropertyRules,
    conditionalPropertyOptionsRules,
    cachedPropertyValues
  } = usePropertiesFormApi({
    objectTypeId,
    objectId,
    fetchConditionalPropertyRules
  });
  const getFormValue = useGetFormValue({
    cachedPropertyValues,
    getSavedValue,
    objectId,
    objectTypeId,
    values
  });
  const handlePropertyValidationChange = useCallback(validation => {
    setPropertyValidations(prev => new Map(prev).set(validation.propertyName, validation));
  }, []);
  const conditionalPropertyDependencies = useConditionalPropertyDependencies({
    loading,
    error,
    objectTypeId,
    getFormValue,
    properties,
    conditionalPropertyRules
  });
  const currentProperties = propertiesRef.current;
  const nodes = useMemo(() => {
    return currentProperties.filter(property =>
    // Protects the form in case a requested property does not have an available definition
    Object.prototype.hasOwnProperty.call(properties, property.propertyName)).map(toFormNode);
  }, [currentProperties, properties]);
  const nodesWithConditionalProperties = useConditionalPropertyRules({
    nodes,
    changedProperties,
    alwaysShowConditionalProperties,
    conditionalPropertyDependencies
  });
  const {
    activeRules,
    nodesWithConditionalOptions,
    resetConditionalPropertyOptionsRules
  } = useConditionalPropertyOptionsRules({
    conditionalPropertyOptionsRules,
    nodes: nodesWithConditionalProperties,
    getFormValue,
    properties,
    changedProperties,
    dependencies: conditionalPropertyDependencies,
    alwaysShowConditionalPropertyOptionsWithErrors
  });
  const {
    validationState,
    getNodeValidation
  } = useFormValidation({
    alwaysShowConditionalPropertyOptionsWithErrors,
    activeRules,
    changedProperties,
    formLoading: loading,
    formError: error,
    propertyValidations,
    properties,
    getFormValue,
    nodes: nodesWithConditionalOptions
  });
  const getNodeValidationRef = useRef(getNodeValidation);
  getNodeValidationRef.current = getNodeValidation;
  const autosaveContext = useContext(AutosaveContext);
  const registerHandler = autosaveContext === null || autosaveContext === void 0 ? void 0 : autosaveContext.registerHandler;
  useEffect(() => {
    if (enableAutosave) {
      return registerHandler === null || registerHandler === void 0 ? void 0 : registerHandler({
        id: formId,
        getIsHandlerForRequest: request => {
          var _request$formMetadata;
          return ((_request$formMetadata = request.formMetadata) === null || _request$formMetadata === void 0 ? void 0 : _request$formMetadata.formId) === formId;
        },
        getIsValidRequest: request => {
          const requestNodeId = request.formMetadata.formNodeId;
          const validation = getNodeValidationRef.current(requestNodeId);
          return (validation === null || validation === void 0 ? void 0 : validation.status) === 'VALID';
        }
      });
    }
  }, [formId, enableAutosave, registerHandler]);
  useEffect(() => {
    // Track the number of top-level properties in the form when it is first invoked
    const initialPropertiesCount = Object.keys(propertiesUnstable).length;
    Metrics.histogram('propertiesform-initial-properties-count').update(initialPropertiesCount);
    // We intentionally only want to track this once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const getPropertyRoots = useMemo(() => {
    const cache = new Map();
    return propertyNames => {
      const roots = {};
      for (const name of propertyNames) {
        if (!cache.has(name)) {
          cache.set(name, getAllPropertyRoots(nodesWithConditionalOptions, name));
        }
        roots[name] = cache.get(name);
      }
      return roots;
    };
  }, [nodesWithConditionalOptions]);

  // wrapper to update current values and pass to host apps
  const handleChange = useCallback(changes => {
    const propertyNames = Object.keys(changes);
    setChangedProperties(current => new Set([...current, ...propertyNames]));
    onChangeRef.current(Object.assign({}, valuesRef.current, changes), {
      roots: getPropertyRoots(propertyNames)
    });
  }, [getPropertyRoots]);
  const handleAutosave = useMemo(() => {
    if (!enableAutosave || !objectId) {
      return undefined;
    }
    return updates => {
      const updatesForObject = updates[objectId];
      if (!updatesForObject) {
        return;
      }
      const updatedProperties = Object.keys(updatesForObject);
      setInitialValuesCache(prev => {
        const next = Object.assign({}, prev);
        for (const propertyName of updatedProperties) {
          next[propertyName] = updatesForObject[propertyName];
        }
        return next;
      });
      onSave === null || onSave === void 0 || onSave(updates);
    };
  }, [enableAutosave, objectId, onSave]);
  const inputs = useMemo(() => {
    if (loading || error) {
      return emptyInputs;
    }
    const newInputs = [];
    for (const node of nodesWithConditionalOptions) {
      const newFormInput = makeNewFormInput({
        properties,
        node,
        enableAutosave,
        formId,
        getFormValue,
        getNodeValidation,
        handleChange,
        handleSave: handleAutosave,
        handleValidationChange: handlePropertyValidationChange,
        objectId,
        objectTypeId,
        inputMode,
        showPropertyDescription
      });
      if (newFormInput !== undefined) {
        newInputs.push(newFormInput);
      }
    }
    return newInputs;
  }, [loading, error, nodesWithConditionalOptions, properties, enableAutosave, formId, getFormValue, getNodeValidation, handleAutosave, handleChange, handlePropertyValidationChange, objectId, objectTypeId, inputMode, showPropertyDescription]);
  useEffect(() => {
    const currentNodes = nodesWithConditionalOptions;
    const prevNodes = formNodesRef.current;
    formNodesRef.current = currentNodes;
    if (prevNodes === currentNodes) {
      // nothing to do; only check for changes if the list of dependents changed
      return;
    }
    const prevDependents = getDependentFormProperties(prevNodes);
    const dependents = getDependentFormProperties(currentNodes);
    const allPropertyNames = getFormPropertyNames(currentNodes);
    const {
      added,
      removed
    } = getFormPropertiesDifference(prevDependents, dependents);

    // when a dependent property appears:
    // * check to see if host app already provides a value (proactively)
    //   for the newly-visible property
    // * if yes, store that value in `initialValuesCache` so that when
    //   the property disappears, we can revert to the original value
    // * if not, store nothing in `initialValuesCache`
    if (added.length) {
      setInitialValuesCache(prev => {
        const next = Object.assign({}, prev);
        for (const propertyName of added) {
          if (Object.prototype.hasOwnProperty.call(values, propertyName)) {
            next[propertyName] = values[propertyName];
          }
        }
        return next;
      });
    }

    // when a dependent property disappears:
    // * if the property is present elsewhere on the form, do nothing
    // * check to see if the host app provides a value for the hidden
    //   property
    // * if yes, revert its value to whatever value was provided by the host
    //   app at the time the property appeared (if any)
    // * if no, remove the value from the draft values entirely
    if (removed.length) {
      // properties which no longer appear anywhere in the form
      const allOccurrencesRemoved = removed.filter(propertyName => !allPropertyNames.has(propertyName));
      let triggerOnChange = false;
      const newValues = Object.assign({}, valuesRef.current);
      for (const propertyName of allOccurrencesRemoved) {
        if (!Object.prototype.hasOwnProperty.call(newValues, propertyName)) {
          // host app did not provide a value for this property and the user did not edit it - no action needed
          continue;
        }
        if (Object.prototype.hasOwnProperty.call(initialValuesCache, propertyName)) {
          // host app provided a value for this property when it appeared - revert to that value
          newValues[propertyName] = initialValuesCache[propertyName];
        } else {
          // host app did not provide a value for this property but the user edited it - we are safe to throw away the value
          delete newValues[propertyName];
        }
        triggerOnChange = true;
      }
      if (triggerOnChange) {
        onChangeRef.current(newValues, {
          roots: getPropertyRoots(Object.keys(newValues))
        });
      }
      setPropertyValidations(prev => {
        const next = new Map(prev);
        // if property is still in the form as a root input - no action needed
        for (const propertyName of allOccurrencesRemoved) {
          next.delete(propertyName);
        }
        return next;
      });
      setChangedProperties(prev => {
        const next = new Set(prev);
        // if property is still in the form as a root input - no action needed
        for (const propertyName of allOccurrencesRemoved) {
          next.delete(propertyName);
        }
        return next;
      });
      setInitialValuesCache(prev => {
        const next = Object.assign({}, prev);
        for (const propertyName of removed) {
          delete next[propertyName];
        }
        return next;
      });
    }
  }, [getPropertyRoots, initialValuesCache, nodesWithConditionalOptions, values]);
  const reset = useCallback(() => {
    setPropertyValidations(new Map());
    setChangedProperties(new Set());
    setInitialValuesCache({});
    resetConditionalPropertyOptionsRules();
    formNodesRef.current = [];
  }, [resetConditionalPropertyOptionsRules]);
  return {
    status: validationState,
    inputs,
    reset
  };
};
export default useNewPropertiesForm;